import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './AppIcon';
import Button from './ui/Button';

const CrisisOverlay = ({ isVisible, onClose }) => {
  if (!isVisible) return null;

  const emergencyContacts = [
    {
      name: 'National Suicide Prevention Lifeline',
      number: '988',
      description: '24/7 crisis support - call or text',
      icon: 'Phone',
      action: 'tel:988',
      actionText: 'Call 988',
    },
    {
      name: 'Emergency Services',
      number: '911',
      description: 'Immediate emergency assistance',
      icon: 'AlertCircle',
      action: 'tel:911',
      actionText: 'Call 911',
    },
    {
      name: 'Crisis Text Line',
      number: 'Text HOME to 741741',
      description: 'Text-based crisis support',
      icon: 'MessageSquare',
      action: 'sms:741741?body=HOME',
      actionText: 'Text Now',
    },
  ];

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="w-full max-w-2xl glass rounded-2xl border-2 border-error/50 overflow-hidden"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
        >
          {/* Header */}
          <div className="bg-error/20 px-6 py-4 border-b border-error/30">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-error/30 flex items-center justify-center animate-pulse">
                <Icon name="Heart" size={24} color="#EF4444" />
              </div>
              <div>
                <h2 className="font-heading font-semibold text-xl text-foreground">
                  We're Here for You
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Your safety is our priority
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-foreground mb-6 leading-relaxed">
              We've noticed you may be going through a difficult time. Please know that you're not alone, 
              and <strong className="text-primary">immediate professional help is available</strong>. 
              These trained counselors are ready to support you right now.
            </p>

            {/* Emergency Contacts */}
            <div className="space-y-4 mb-6">
              {emergencyContacts.map((contact, index) => (
                <div
                  key={index}
                  className="bg-error/10 rounded-xl p-4 border border-error/20 hover:border-error/40 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-error/20 flex items-center justify-center flex-shrink-0">
                      <Icon name={contact.icon} size={20} color="#EF4444" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading font-medium text-foreground mb-1">
                        {contact.name}
                      </h3>
                      <p className="font-mono text-lg font-bold text-error mb-1">
                        {contact.number}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {contact.description}
                      </p>
                    </div>
                    <a
                      href={contact.action}
                      className="px-4 py-2 bg-error text-error-foreground rounded-lg hover:bg-error/90 transition-colors font-medium text-sm"
                    >
                      {contact.actionText}
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* Additional Info */}
            <div className="glass rounded-xl p-4 mb-6 border border-border">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} color="#FFD700" className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-foreground leading-relaxed">
                    Professional counselors are trained to help with crisis situations. They provide 
                    confidential, compassionate support and can connect you with additional resources 
                    if needed. <strong>You matter, and help is available.</strong>
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Return to Reflection
              </Button>
              <a
                href="tel:988"
                className="flex-1"
              >
                <Button
                  variant="destructive"
                  fullWidth
                  className="justify-center"
                >
                  <Icon name="Phone" size={18} className="mr-2" />
                  Call 988 Now
                </Button>
              </a>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CrisisOverlay;
